import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

import { AuthorizedImage } from '@/components/AuthorizedImage';

import styles from './ImageCarousel.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CarouselImage =
  | { type: 'url'; src: string }
  | { type: 'file'; file: File; preview: string }
  /** `thumb` is the slide-sized variant of `src`; the preview opens `src` itself. */
  | { type: 'attachment'; id: string; src: string; thumb?: string; name: string };

export function getCarouselImageSrc(img: CarouselImage): string {
  return img.type === 'file' ? img.preview : img.src;
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface CarouselContextValue {
  trackRef: React.RefObject<HTMLDivElement | null>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollPrev: () => void;
  scrollNext: () => void;
}

const CarouselContext = createContext<CarouselContextValue | null>(null);

function useCarousel(): CarouselContextValue {
  const ctx = useContext(CarouselContext);
  if (!ctx) throw new Error('Carousel compound components must be used inside <Carousel>');
  return ctx;
}

// ─── Root ─────────────────────────────────────────────────────────────────────

interface CarouselProps {
  children: ReactNode;
  className?: string;
}

function Carousel({ children, className }: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const scrollPrev = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  const scrollNext = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  return (
    <CarouselContext.Provider
      value={{ trackRef, canScrollLeft, canScrollRight, scrollPrev, scrollNext }}
    >
      <div className={[styles.root, className].filter(Boolean).join(' ')}>{children}</div>
    </CarouselContext.Provider>
  );
}

// ─── Track ────────────────────────────────────────────────────────────────────

interface TrackProps {
  children: ReactNode;
  className?: string;
}

function Track({ children, className }: TrackProps) {
  const { trackRef } = useCarousel();
  return (
    <div ref={trackRef} className={[styles.track, className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

// ─── Slide ────────────────────────────────────────────────────────────────────

interface SlideProps {
  src: string;
  alt?: string;
  onDelete?: () => void;
  onClick?: () => void;
  className?: string;
}

function Slide({ src, alt = '', onDelete, onClick, className }: SlideProps) {
  return (
    <div className={[styles.slide, className].filter(Boolean).join(' ')}>
      <AuthorizedImage
        src={src}
        alt={alt}
        className={styles.image}
        draggable={false}
        onClick={onClick}
        style={onClick ? { cursor: 'pointer' } : undefined}
      />
      {onDelete && (
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={onDelete}
          aria-label="Remove image"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path
              d="M1 1L9 9M9 1L1 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Prev / Next ──────────────────────────────────────────────────────────────

interface NavProps {
  children?: ReactNode;
  className?: string;
}

function Prev({ children, className }: NavProps) {
  const { scrollPrev, canScrollLeft } = useCarousel();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        scrollPrev();
      }}
      disabled={!canScrollLeft}
      className={[styles.navBtn, styles.navBtnPrev, className].filter(Boolean).join(' ')}
      aria-label="Scroll left"
    >
      {children ?? <MdChevronLeft size={20} />}
    </button>
  );
}

function Next({ children, className }: NavProps) {
  const { scrollNext, canScrollRight } = useCarousel();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        scrollNext();
      }}
      disabled={!canScrollRight}
      className={[styles.navBtn, styles.navBtnNext, className].filter(Boolean).join(' ')}
      aria-label="Scroll right"
    >
      {children ?? <MdChevronRight size={20} />}
    </button>
  );
}

// ─── Compound export ──────────────────────────────────────────────────────────

Carousel.Track = Track;
Carousel.Slide = Slide;
Carousel.Prev = Prev;
Carousel.Next = Next;

export { Carousel };
